import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { fetchMemberReportList, fetchTeamReportList, updateTeamOrder } from '../../../states/containers/report/reportSlice'
import { RootState } from '../../../states/containers/store'
import { format, nowDate } from '../../../utils/date'
import { isManager } from '../../../utils/role'
import { getGetPrams, setPath } from '../../../utils/url'
import Loading from '../../components/common/Loading'
import MemberList from '../../components/report/index/MemberList'
import MemberModal from '../../components/report/index/MemberModal'
import TeamList from '../../components/report/index/TeamList'
import { URL } from '../../constants/url'

import {
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DragDropContext,
  Droppable,
  Draggable,
} from "react-beautiful-dnd"
import { uniqueId } from 'lodash'

type LangValue = 'ja' | 'en'

interface MemberDetail {
  member_id: number
  member_name: string
  year: string
}
interface GetParams {
  year: string
}

const Index: FC = () => {
  const location = useLocation()
  const { language } = useParams<{ language: LangValue }>()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()

  const {
    login,
    report,
    commonPart,
  } = useSelector((state: RootState) => state)

  const [targetYear, setTargetYear] = useState<string>("")

  const [isMemberModalDisplay, setIsMemberModalDisplay] = useState<boolean>(false)

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const [isSortDisplay, setIsSortDisplay] = useState(false)

  const [isSort, setIsSort] = useState(false)

  const [teamList, setTeamList] = useState<{ id: string, name: string }[]>([])

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const newTeamList = [...teamList]

    const [removed] = newTeamList.splice(result.source.index, 1)
    newTeamList.splice(result.destination.index, 0, removed)
    const teamListString = teamList.map((item) => item.id).join(',')
    const newTeamListString = newTeamList.map((item) => item.id).join(',')
    if (teamListString !== newTeamListString) {
      setTeamList(newTeamList)
      dispatch(updateTeamOrder(
        {
          team_order: newTeamList.map((item) => Number(item.id)),
        },
      ))
      setIsSort(true)
    }
  }

  const memberModalDetailInit = {
    member_id: 0,
    member_name: "",
    year: targetYear,
  }

  const [memberModalDetail, setMemberModalDetail] = useState<MemberDetail>(memberModalDetailInit)

  const openMemberModal = (memberDetail: MemberDetail) => {
    setMemberModalDetail({
      member_id: memberDetail.member_id,
      member_name: memberDetail.member_name,
      year: targetYear,
    })
    setIsMemberModalDisplay(true)
    dispatch(fetchMemberReportList({ member_id: memberDetail.member_id, year: targetYear }))
  }

  const handleChangeYear = (value: number) => {
    setIsCalendarDisplay(false)
    setTargetYear(value.toString())
    history.push(`${setPath(URL.REPORT.path, language)}?year=${value}`)
  }

  const yearList = useMemo(() => {
    if (!commonPart.company?.created_at) return []
    const startYear = Number(format(commonPart.company.created_at, "YYYY"))
    const endYear = Number(nowDate("YYYY"))

    return Array((endYear - startYear) + 1).fill(null).map((_, i) => (
      startYear + i
    )).reverse()
  }, [commonPart])

  useEffect(() => {
    if (!report.teamReportList) return
    setTeamList(report.teamReportList?.teams.map((team) => ({ id: team.id.toString(), name: team.name })))
  }, [report.teamReportList?.teams])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (!getParams.year) {
      setTargetYear(format(nowDate(), "YYYY"))
    } else {
      setTargetYear(getParams.year)
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!login.accessToken || !targetYear) return
    if (isManager(login.role)) {
      dispatch(fetchTeamReportList({ year: targetYear }))
    } else {
      dispatch(fetchMemberReportList({ member_id: login.current_member_id || 0, year: targetYear }))
    }
  }, [login.accessToken, targetYear])

  useEffect(() => {
    if (isSortDisplay || !isSort) return
    dispatch(fetchTeamReportList({ year: targetYear }))
    setIsSort(false)
  }, [isSortDisplay])

  return (
    <>
      {
        report.isLoading ? (
          <Loading />
        ) : (
          <section className="report_page">
            <Helmet>
              <title>Qasee - AI業務分析</title>
            </Helmet>
            <section className="page_header">
              <div className="title" />
              <div className="tools">
                <div className="calendar_text">
                  <button
                    type="button"
                    className="display button"
                    onClick={() => setIsCalendarDisplay(true)}
                  >
                    {targetYear}
                    {t('report.list.header.year')}
                    <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
                  </button>
                  <button
                    type="button"
                    className="prev button"
                    onClick={() => handleChangeYear(Number(targetYear) - 1)}
                    disabled={
                      !commonPart.company?.created_at
                      || (Number(targetYear)) === Number(format(commonPart.company?.created_at, 'YYYY'))
                    }
                  >
                    <FontAwesomeIcon icon={['fas', 'angle-left']} />
                  </button>
                  <button
                    type="button"
                    className="next button"
                    onClick={() => handleChangeYear(Number(targetYear) + 1)}
                    disabled={
                      !commonPart.company?.created_at
                      || (Number(targetYear)) === Number(nowDate('YYYY'))
                    }
                  >
                    <FontAwesomeIcon icon={['fas', 'angle-right']} />
                  </button>
                </div>
                <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
                  <div className="range">
                    <div className="text">
                      {t('report.list.header.select_year')}
                    </div>
                  </div>
                  <div className="monthly">
                    <div
                      style={{
                        textAlign: "center",
                      }}
                    >
                      {
                        yearList.map((year) => (
                          <button
                            type="button"
                            onClick={() => handleChangeYear(year)}
                            className="year"
                            key={uniqueId()}
                          >
                            {year}
                            {t('report.list.header.year')}
                          </button>
                        ))
                      }
                    </div>
                  </div>
                </div>
                <div className={`close_screen gray ${isCalendarDisplay || 'none'}`} role="presentation" onClick={() => setIsCalendarDisplay(false)} />
                {
                  (isManager(login.role)) && (
                    <>
                      <div className="spacer" />
                      <div>
                        <button
                          type="button"
                          className="display button"
                          onClick={() => setIsSortDisplay(true)}
                        >
                          <FontAwesomeIcon icon={['fas', 'sort-amount-down']} />
                        </button>
                      </div>
                      <div
                        className={`dnd_box ${isSortDisplay || 'none'}`}
                      >
                        <div className="title">
                          {t('report.list.header.sort')}
                        </div>
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="droppable">
                            {(provided: DroppableProvided) => (
                              <div
                                className="item_list"
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                              >
                                {
                                  teamList.map(({ id, name }, index) => (
                                    <Draggable key={id} draggableId={id} index={index}>
                                      {(provided_dg: DraggableProvided) => (
                                        <div
                                          className="item"
                                          ref={provided_dg.innerRef}
                                          {...provided_dg.dragHandleProps}
                                          {...provided_dg.draggableProps}
                                        >
                                          <div>
                                            {name}
                                          </div>
                                          <div>
                                            <FontAwesomeIcon icon={['fas', 'sort']} />
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  ))
                                }
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </div>
                      <div className={`close_screen gray ${isSortDisplay || 'none'}`} role="presentation" onClick={() => setIsSortDisplay(false)} />
                    </>
                  )
                }
              </div>
            </section>
            {
              report.isLoading ? (
                <Loading />
              ) : (
                <>
                  {
                    isManager(login.role) ? (
                      report.teamReportList?.teams.map((team) => (
                        <section className="page_contents list_box" key={`${team.id}_team`}>
                          <TeamList
                            team={team}
                            year={targetYear}
                            openMemberModalCallback={(param) => openMemberModal(param)}
                          />
                        </section>
                      ))
                    ) : (
                      <section className="page_contents list_box">
                        <MemberList
                          reports={report.memberReportList?.reports || []}
                          id={commonPart.current_member?.id || 0}
                          name={commonPart.current_member?.name || ""}
                          year={targetYear}
                        />
                      </section>
                    )
                  }
                  <MemberModal
                    detail={memberModalDetail}
                    isDisplay={isMemberModalDisplay}
                    closeCallback={() => setIsMemberModalDisplay(false)}
                  />
                </>
              )
            }
          </section>
        )
      }
    </>
  )
}

export default Index
